import {
    collection,
    doc,
    DocumentData,
    onSnapshot,
    query,
    QueryConstraint,
    QuerySnapshot,
    setDoc,
    Timestamp,
    Unsubscribe,
    where,
    getDocs,
    FirestoreError,
    serverTimestamp,
    getDoc,
} from 'firebase/firestore';
import dayjs from 'dayjs';
import { Appointment } from '../../api/fetchAppointmentById';
import { getDoctorSelectorList, getGById, getMe, updateG } from '../../store/idbHandler';
import { firestore } from '../index';
import { DOCTOOL_FIRESTORE_TABLES } from '../constants';
import { getDefiniteMActiveId } from '../../store/localstorage';
import { DB_TABLES } from '../../store/dbTables';

export type FirebaseAppointment = Omit<
    Appointment,
    | 'start_time'
    | 'end_time'
    | 'date'
    | 'full_date'
    | 'visit_start'
    | 'visit_checkin'
    | 'visit_end'
    | 'follow_up'
> & {
    start_time: Timestamp;
    end_time: Timestamp;
    date: Timestamp;
    full_date: Timestamp;
    visit_checkin: Timestamp;
    visit_start: Timestamp;
    visit_end: Timestamp;
    follow_up: Timestamp | null;
    is_local: boolean;
};

export type OfflineAppointment = Omit<
    FirebaseAppointment,
    'full_date' | 'date' | 'start_time' | 'end_time'
> & {
    full_date: number;
    start_time: number;
    end_time: number;
    date: number;
};

export const appointmentRef = collection(firestore, DOCTOOL_FIRESTORE_TABLES.APPOINTMENT);

export default async function setupAppointmentListener({
    clinicId,
    date,
    onAptSnapshot,
    onError,
    docids,
}: {
    clinicId: string;
    date: Date;
    onAptSnapshot?: (snapshot: QuerySnapshot<DocumentData, DocumentData>) => void;
    onError?: (error: FirestoreError) => void;
    docids?: string[];
}): Promise<Unsubscribe> {
    const doctorList =
        docids ||
        getDoctorSelectorList().reduce<string[]>((acc, currentDoctor) => {
            if (!!currentDoctor.professional.clinics?.some((clinic) => clinic._id === clinicId)) {
                acc.push(currentDoctor.id);
            }

            return acc;
        }, []);

    const docIds = doctorList;

    const queries: QueryConstraint[] = [
        where('bid', '==', getMe()?.business?.id || `b-${getMe()?.doctor?._id}`),
        where('full_date', '>=', Timestamp.fromDate(dayjs(date).startOf('day').toDate())),
        where('full_date', '<=', Timestamp.fromDate(dayjs(date).endOf('day').toDate())),
        where('archive', '==', false),
    ];

    if (clinicId && clinicId !== 'all') {
        queries.push(where('clinic_id', '==', clinicId));
        const queryDocs = Array.from(
            new Set([...docIds, getDocIdFromClinicIdForApt({ clinicId })]),
        );
        queries.push(where('doctor_oid', 'in', queryDocs));
    } else if (clinicId) {
        // getMe()?.clinics?.forEach((clinic) => {
        //     docIds.push(getDocIdFromClinicIdForApt({ clinicId: clinic.id }));
        // });
        queries.push(where('doctor_oid', 'in', docIds));
    }

    console.log('🚀 ~ file: index.ts:78 ~ queries', queries);

    const aptQuery = query(appointmentRef, ...queries);

    return onSnapshot(
        aptQuery,
        { includeMetadataChanges: true },
        (snapshot) => {
            console.log('🚀 ~ file: index.ts:78 ~ snapshot:', snapshot);

            onAptSnapshot?.(snapshot);
        },
        (error) => {
            console.error('Error in apt snapshot', error);
            onError?.(error);
        },
        () => {
            console.error('Snapshot listener completed');
        },
    );
}

export async function updateAppointment({
    aid,
    data,
}: {
    aid: string;
    data: Partial<FirebaseAppointment>;
}): Promise<unknown> {
    const appointmentUpdates = removeKeysHavingUndefined({
        ...data,
        updated_at: serverTimestamp(),
    });

    console.log(
        '🚀 ~ file: index.ts:96 ~ appointmentUpdates:updateAppointment',
        aid,
        appointmentUpdates,
    );

    await setDoc(doc(appointmentRef, aid), appointmentUpdates, {
        merge: true,
    }).catch((e) => console.error('error: appointmentUpdates:updateAppointment', e));

    return appointmentUpdates;
}

export async function createAppointment({
    data,
    aid,
}: {
    data: FirebaseAppointment;
    aid: string;
}): Promise<unknown> {
    const appointmentDocument = removeKeysHavingUndefined({
        ...data,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
    });

    console.log(
        '🚀 ~ file: index.ts:110 ~ appointmentDocument:createAppointment',
        appointmentDocument,
        aid,
    );

    await setDoc(doc(appointmentRef, aid), appointmentDocument).catch(console.error);
    // data
    if (appointmentDocument.mode === 'tele' && data.vc_meta?.platform === 'eka') {
        if (data.vc_meta) {
            writeTeleV2UrlsInFS(aid, data.vc_meta.host_link!, data.vc_meta.meet_link!, false);
        }
    }
    console.log('🚀 ~ file: index.ts:110 ~ appointmentDocument:AFTER createAppointment');
    return appointmentDocument;
}

export const writeTeleV2UrlsInFS = async (
    aid: string,
    host_link: string,
    meet_link: string,
    isTeleCallOnDemand: boolean,
) => {
    if (!aid || !host_link || !meet_link) {
        return;
    }
    try {
        const teledocRef = doc(appointmentRef, aid, 'tele', aid);

        await setDoc(
            teledocRef,
            {
                host_url: host_link,
                guest_url: meet_link,
            },
            { merge: true },
        );
        if (isTeleCallOnDemand) {
            const aidDocRef = doc(appointmentRef, aid);
            await setDoc(
                aidDocRef,
                {
                    vc_meta: {
                        host_link,
                        meet_link,
                        platform: 'eka',
                    },
                },
                { merge: true },
            );
        }
    } catch (firestoreError) {
        console.error('ERROR ======> updateTeleRoomsDetailsInFS', firestoreError);
    }
};
export async function getAppointmentById(aid: string): Promise<OfflineAppointment | null> {
    try {
        const apt = await getGById<OfflineAppointment | undefined>(
            getDefiniteMActiveId(),
            DB_TABLES.appointments,
            aid,
        );
        if (apt) return apt;
        const docRef = doc(appointmentRef, aid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const offlineApt: OfflineAppointment = window.firebaseAppointmentToOfflineAppointment(
                aid,
                docSnap,
            );
            updateG(getDefiniteMActiveId(), DB_TABLES.appointments, [offlineApt]).catch((err) => {
                console.log(err);
            });
            return offlineApt;
        } else {
            console.warn(`No appointment found with id: ${aid}`);
            return null;
        }
    } catch (error) {
        console.error('Error fetching appointment by ID:', error);
        return null;
    }
}

export function getDocIdFromClinicIdForApt({ clinicId }: { clinicId: string }): string {
    return `c-${clinicId}`;
}

export async function queryAppointments({
    clinicId,
    doctorIds,
    date,
    getArchived,
    status,
    patientOid,
}: {
    clinicId: string;
    doctorIds: string[];
    date: Date;
    getArchived?: boolean;
    status?: string[];
    patientOid?: string;
}): Promise<QuerySnapshot<DocumentData, DocumentData> | undefined> {
    try {
        const archivedVals = [false];
        if (getArchived) {
            archivedVals.push(true);
        }

        doctorIds.push(getDocIdFromClinicIdForApt({ clinicId }));

        const queries = [
            where('bid', '==', getMe()?.business?.id || `b-${getMe()?.doctor?._id}`),
            where('clinic_id', '==', clinicId),
            where(
                'full_date',
                '>=',
                Timestamp.fromMillis(dayjs(date).startOf('day').unix() * 1000),
            ),
            where('full_date', '<=', Timestamp.fromMillis(dayjs(date).endOf('day').unix() * 1000)),
            where('archive', 'in', archivedVals),
            where('doctor_oid', 'in', doctorIds),
        ];

        if (status) {
            queries.push(where('status', 'in', status));
        }

        if (patientOid) {
            queries.push(where('patient_oid', '==', patientOid));
        }

        const appointmentQuery = query(appointmentRef, ...queries);
        const docs = await getDocs(appointmentQuery);
        return docs;
    } catch (e) {
        console.error(e);
    }
}

export function removeKeysHavingUndefined<OutputType = any>(
    input: Record<string, unknown>,
): OutputType {
    const filteredObj = Object.keys(input).reduce<Record<string, unknown>>((acc, key) => {
        if (input[key] !== undefined) {
            acc[key] = input[key];
        }
        return acc;
    }, {});

    return filteredObj as OutputType;
}
